import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useEditQuestionForm, useQuestion } from "./hooks";
import { useRemoveQuestion, useSurveySavedNotification } from "../../hooks";
import { useDriverTitle } from "@pulse/hooks/taxiDriverHooks";
import { useQuery } from "@tanstack/react-query";
import { driversQueryOptions } from "@pulse/api/queryApi";
import { useMemo } from "react";
import { QuestionType } from "@pulse/api/surveyApi";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
    Button,
    Card,
} from "frontend-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash } from "@fortawesome/sharp-regular-svg-icons";

function QuestionNumber({ value }: { value: number }) {
    return (
        <Card
            surface="greige"
            className="h-7 min-w-7 shrink-0 items-center justify-center px-2 py-0 text-sm font-medium"
        >
            {value}
        </Card>
    );
}

function InactiveQuestionLayout({ children }: { children: ReactNode }) {
    const { question, idx } = useQuestion();
    const { t } = useTranslation();
    const driverTFn = useDriverTitle();
    const { data: drivers } = useQuery(driversQueryOptions());
    const driver = useMemo(
        () => (drivers ?? []).find((driver) => driver.id === question.driverId),
        [question.driverId, drivers],
    );
    const removeQuestion = useRemoveQuestion(question.id);
    const surveySavedNotification = useSurveySavedNotification();
    const handleRemove = async () => {
        const promise = removeQuestion.mutateAsync();
        surveySavedNotification(promise);
    };

    return (
        <li className="flex w-full flex-col items-start gap-3">
            {driver && (
                <span className="text-category tracking-category text-compendia-muted font-medium uppercase">
                    {driverTFn(driver)}
                </span>
            )}
            <div className="relative border-compendia-border bg-compendia-greige-10 rounded-card flex flex-col lg:flex-row w-full items-start gap-3 border p-6 shadow-sm">
                <QuestionNumber value={idx + 1} />
                <div className="flex min-w-0 flex-1 flex-col items-start gap-3">
                    <div className="flex flex-row w-full justify-between">
                        <span className="text-base w-full wrap-break-word">
                            {question.questionText}
                            {!question.optional && <span className="text-chameleon-pink"> *</span>}
                        </span>
                        <div
                            className=" absolute h-7 items-center flex top-6 right-6 lg:static lg:ml-3"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <AlertDialog>
                                <AlertDialogTrigger
                                    render={
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            className="text-compendia-muted hover:text-chameleon-pink p-0"
                                        />
                                    }
                                >
                                    <FontAwesomeIcon
                                        icon={faTrash}
                                        className="text-chameleon-pink "
                                    />
                                </AlertDialogTrigger>

                                <AlertDialogContent>
                                    <AlertDialogHeader>
                                        <AlertDialogTitle>
                                            {t("question.deleteTitle")}
                                        </AlertDialogTitle>
                                        <AlertDialogDescription>
                                            {t("question.deleteDescription")}
                                        </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                        <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                                        <AlertDialogAction onClick={handleRemove}>
                                            {t("question.remove")}
                                        </AlertDialogAction>
                                    </AlertDialogFooter>
                                </AlertDialogContent>
                            </AlertDialog>
                        </div>
                    </div>

                    {children}
                    {question.allowComments && (
                        <div className="text-compendia-muted mt-1 text-sm italic">
                            {t("question.optional")}
                        </div>
                    )}
                </div>
            </div>
        </li>
    );
}

function ActiveQuestionLayout({ children }: { children: ReactNode }) {
    const { t } = useTranslation();
    const { question, idx } = useQuestion();
    const removeQuestion = useRemoveQuestion(question.id);
    const surveySavedNotification = useSurveySavedNotification();
    const form = useEditQuestionForm();

    const handleRemove = async () => {
        const promise = removeQuestion.mutateAsync();
        surveySavedNotification(promise);
    };

    const allowsComments = question.questionType !== QuestionType.TEXT;

    return (
        <li className="flex w-full flex-col lg:flex-row items-start gap-3">
            <QuestionNumber value={idx + 1} />
            <div className="flex w-full min-w-0 flex-1 flex-col items-start gap-4">
                <form.AppField name="driverId">{(field) => <field.Taxonomy />}</form.AppField>
                {children}
                <div className="flex w-full flex-wrap items-center justify-between gap-4">
                    <div className="flex flex-wrap items-center gap-6">
                        <form.AppField name="optional">
                            {(field) => <field.RequiredCheckbox />}
                        </form.AppField>
                        {allowsComments && (
                            <form.AppField name="allowComments">
                                {(field) => <field.CommentSectionCheckbox />}
                            </form.AppField>
                        )}
                    </div>
                    <Button
                        type="button"
                        variant="ghost"
                        className="text-compendia-muted hover:text-chameleon-pink"
                        onClick={handleRemove}
                    >
                        {t("question.remove")}
                    </Button>
                </div>
            </div>
        </li>
    );
}

export { InactiveQuestionLayout, ActiveQuestionLayout };
